'use client';
import { useMemo, useState } from 'react';
import { Box, Card, Stack, Typography, Chip } from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import DataTableV2 from '../../../components/DataTableV2';
import dayjs from 'dayjs';
import { useLanguage } from '../../../utils/i18n';

export default function StatusOperational({ vehicleStatus = [], rawTrips = [], loading = false }) {
  const { t } = useLanguage();
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 5 });
  const [activeFilter, setActiveFilter] = useState('All');
  const [searchConfig, setSearchConfig] = useState({ field: 'all', value: '' });

  const { processedData, summary } = useMemo(() => {
    let all = 0,
      moving = 0,
      off = 0,
      idle = 0,
      maintenance = 0,
      parking = 0,
      delay = 0;

    if (!vehicleStatus || vehicleStatus.length === 0) {
      return { processedData: [], summary: { all, moving, off, idle, maintenance, parking, delay } };
    }

    const ritaseMapById = {};
    const ritaseMapByPlate = {};
    if (rawTrips && rawTrips.length > 0) {
      rawTrips.forEach((trip) => {
        const vId = trip.vehicle_id;
        const plate = String(trip.license_plate || '')
          .trim()
          .toLowerCase();

        const count = trip.ritase_count || trip.details?.length || 1;

        if (vId) {
          if (!ritaseMapById[vId]) ritaseMapById[vId] = 0;
          ritaseMapById[vId] += count;
        }
        if (plate) {
          if (!ritaseMapByPlate[plate]) ritaseMapByPlate[plate] = 0;
          ritaseMapByPlate[plate] += count;
        }
      });
    }

    const formattedRows = vehicleStatus.map((item, index) => {
      const vId = item.vehicle_id || item.id;
      const plate = item.vehicle_license_plate || item.license_plate || item.vehicle_name || `Unknown-${vId}`;
      const driver = item.raw?.driver_name || item.raw?.driver || '-';

      let condition = 'Unknown';
      let colorChip = 'default';

      const statusRaw = String(item.status || item.raw?.status || '').toLowerCase();
      const engineRaw = String(item.engine || item.engine_status || item.raw?.engine || '').toUpperCase();
      const speed = Number(item.speed || item.raw?.speed || 0);
      const isLoaded = item.isLoaded || item.raw?.isLoaded || false;
      const rowData = item.raw || item;

      const isAtPolygon =
        statusRaw.includes('at pool') ||
        statusRaw.includes('at geofence') ||
        statusRaw.includes('at customer') ||
        statusRaw.includes('at polygon');
      const isOverSpeedSpatial = rowData.spatial_id > 0 && rowData.spatial_max_speed > 0 && speed > rowData.spatial_max_speed && isLoaded;
      const isUnderSpeedSpatial = rowData.spatial_id > 0 && rowData.spatial_min_speed > 0 && speed < rowData.spatial_min_speed && isLoaded;
      const isStatusOverSpeed = statusRaw.includes('over speed') || statusRaw.includes('overspeed');
      const isStatusUnderSpeed = statusRaw.includes('under speed') || statusRaw.includes('underspeed');
      const gpsRaw = String(item.gps || item.raw?.gps || '').toLowerCase();
      const isGpsUpdated = gpsRaw.includes('updated') || gpsRaw.includes('update');

      if ((isStatusOverSpeed || isOverSpeedSpatial || isStatusUnderSpeed || isUnderSpeedSpatial) && isGpsUpdated) {
        condition = 'Moving';
        colorChip = 'success';
        moving++;
      } else if (gpsRaw.includes('delay')) {
        if (gpsRaw.includes('24')) condition = 'Delay > 24h';
        else if (gpsRaw.includes('1')) condition = 'Delay > 1h';
        else condition = 'Delay';
        colorChip = 'error';
        delay++;
      } else if (statusRaw.includes('delay')) {
        if (statusRaw.includes('24')) condition = 'Delay > 24h';
        else if (statusRaw.includes('1')) condition = 'Delay > 1h';
        else condition = 'Delay';
        colorChip = 'error';
        delay++;
      } else if (statusRaw.includes('maintenance')) {
        condition = 'Maintenance';
        colorChip = 'error';
        maintenance++;
      } else if ((engineRaw === 'ON' || engineRaw === 'IDLE') && speed > 0 && isGpsUpdated) {
        condition = 'Moving';
        colorChip = 'success';
        moving++;
      } else if ((engineRaw === 'ON' || engineRaw === 'IDLE') && speed === 0 && isGpsUpdated) {
        condition = 'Idle';
        colorChip = 'warning';
        idle++;
      } else if (engineRaw === 'OFF') {
        if (isAtPolygon) {
          condition = 'Parked';
          colorChip = 'primary';
          parking++;
        } else {
          condition = 'Stopped';
          colorChip = 'default';
          off++;
        }
      } else if (speed > 0 && isGpsUpdated) {
        condition = 'Moving';
        colorChip = 'success';
        moving++;
      } else if (isAtPolygon && isGpsUpdated) {
        condition = 'Parked';
        colorChip = 'primary';
        parking++;
      } else {
        condition = 'Stopped';
        colorChip = 'default';
        off++;
      }

      all++;

      const plateKey = String(plate).trim().toLowerCase();
      const ritaseCount = (vId && ritaseMapById[vId]) || (plateKey && ritaseMapByPlate[plateKey]) || 0;

      return {
        id: vId || `ops-${index}`,
        driverName: driver,
        vehicleNumber: plate,
        statusVehicle: condition,
        colorChip: colorChip,
        lastUpdateRaw: item.last_update,
        retase: ritaseCount
      };
    });

    return { processedData: formattedRows, summary: { all, moving, off, idle, maintenance, parking, delay } };
  }, [vehicleStatus, rawTrips]);

  const filteredData = useMemo(() => {
    let result = processedData;

    if (activeFilter !== 'All') {
      // Need to handle special mapping because activeFilter might be 'OFF' but condition is 'Stopped' etc
      // We will match condition with the activeFilter labels.
      // activeFilter labels: 'All', 'Moving', 'Parking', 'Maintenance', 'Delay', 'IDLE', 'OFF'
      // conditions in data: 'Moving', 'Parked', 'Maintenance', 'Delay', 'Delay > 1h', 'Delay > 24h', 'Idle', 'Stopped', 'Unknown'
      result = result.filter((row) => {
        if (activeFilter === 'Moving') return row.statusVehicle === 'Moving';
        if (activeFilter === 'Parking') return row.statusVehicle === 'Parked';
        if (activeFilter === 'Maintenance') return row.statusVehicle === 'Maintenance';
        if (activeFilter === 'Delay') return row.statusVehicle.includes('Delay');
        if (activeFilter === 'IDLE') return row.statusVehicle === 'Idle';
        if (activeFilter === 'OFF') return row.statusVehicle === 'Stopped';
        return true;
      });
    }

    if (searchConfig.value) {
      const lowerVal = searchConfig.value.toLowerCase();
      result = result.filter((row) => {
        if (searchConfig.field === 'all') {
          const combined = `${row.driverName} ${row.vehicleNumber} ${row.statusVehicle} ${row.retase} ${
            row.lastUpdateRaw ? dayjs(row.lastUpdateRaw).format('DD MMM YYYY HH:mm:ss') : ''
          }`.toLowerCase();
          return combined.includes(lowerVal);
        } else {
          let fieldVal = String(row[searchConfig.field] || '').toLowerCase();
          if (searchConfig.field === 'lastUpdateRaw' && row.lastUpdateRaw) {
            fieldVal = dayjs(row.lastUpdateRaw).format('DD MMM YYYY HH:mm:ss').toLowerCase();
          }
          return fieldVal.includes(lowerVal);
        }
      });
    }

    return result;
  }, [processedData, activeFilter, searchConfig]);

  const columns = [
    { field: 'driverName', headerName: t('dashboardAsset.driverName', 'Driver Name'), flex: 1.5, minWidth: 150 },
    { field: 'vehicleNumber', headerName: t('dashboardAsset.vehicleNumber', 'Vehicle Number'), flex: 1.2, minWidth: 120 },
    {
      field: 'statusVehicle',
      headerName: t('dashboardAsset.statusVehicle', 'Status Vehicle'),
      flex: 1.2,
      minWidth: 130,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        return (
          <Chip
            label={params.value || t('dashboardAsset.unknown', 'Unknown')}
            color={params.row.colorChip}
            size="small"
            variant="outlined"
            sx={{ fontWeight: 'bold', textTransform: 'capitalize' }}
          />
        );
      }
    },
    {
      field: 'lastUpdateRaw',
      headerName: t('dashboardAsset.lastUpdate', 'Last Update'),
      flex: 1.8,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => {
        if (!params.value) return '-';
        const formatted = dayjs(params.value).format('DD MMM YYYY HH:mm:ss');
        const diffMin = dayjs().diff(dayjs(params.value), 'minute');

        let secondaryText = '';
        if (diffMin < 1) secondaryText = 'A few seconds ago';
        else if (diffMin < 60) secondaryText = `${diffMin} minutes ago`;
        else if (diffMin < 1440) secondaryText = `${Math.floor(diffMin / 60)} hours ago`;
        else secondaryText = `${Math.floor(diffMin / 1440)} days ago`;

        return (
          <Stack direction="column" spacing={0} justifyContent="center" height="100%">
            <Typography variant="body2">{formatted}</Typography>
            <Typography variant="caption" color="text.secondary">
              {secondaryText}
            </Typography>
          </Stack>
        );
      }
    },
    { field: 'retase', headerName: t('dashboardAsset.ritase', 'Ritase'), flex: 0.8, align: 'center', headerAlign: 'center', type: 'number' }
  ];

  return (
    <Card sx={{ p: 2, borderRadius: 3, boxShadow: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              bgcolor: '#485D9A',
              color: 'white',
              borderRadius: 2,
              width: 40,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <LocalShippingIcon />
          </Box>
          <Typography variant="h4" fontWeight="bold">
            {t('dashboardAsset.statusOperational', 'Status Operational')}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Chip
            label={`${t('revenue.all', 'All')}: ${summary.all}`}
            size="small"
            onClick={() => setActiveFilter('All')}
            sx={{
              bgcolor: '#e3f2fd',
              color: '#1976d2',
              fontWeight: 'bold',
              cursor: 'pointer',
              opacity: activeFilter !== 'All' ? 0.5 : 1,
              border: activeFilter === 'All' ? '1px solid #1976d2' : 'none'
            }}
            icon={<Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#1976d2', ml: 1 }} />}
          />
          <Chip
            label={`${t('revenue.moving', 'Moving')}: ${summary.moving}`}
            size="small"
            onClick={() => setActiveFilter('Moving')}
            sx={{
              bgcolor: '#e8f5e9',
              color: '#2e7d32',
              fontWeight: 'bold',
              cursor: 'pointer',
              opacity: activeFilter !== 'Moving' ? 0.5 : 1,
              border: activeFilter === 'Moving' ? '1px solid #2e7d32' : 'none'
            }}
            icon={<Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#2e7d32', ml: 1 }} />}
          />
          <Chip
            label={`${t('revenue.parking', 'Parking')}: ${summary.parking}`}
            size="small"
            onClick={() => setActiveFilter('Parking')}
            sx={{
              bgcolor: '#f3e5f5',
              color: '#9c27b0',
              fontWeight: 'bold',
              cursor: 'pointer',
              opacity: activeFilter !== 'Parking' ? 0.5 : 1,
              border: activeFilter === 'Parking' ? '1px solid #9c27b0' : 'none'
            }}
            icon={<Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#9c27b0', ml: 1 }} />}
          />
          <Chip
            label={`${t('revenue.maintenance', 'Maintenance')}: ${summary.maintenance}`}
            size="small"
            onClick={() => setActiveFilter('Maintenance')}
            sx={{
              bgcolor: '#ffebee',
              color: '#c62828',
              fontWeight: 'bold',
              cursor: 'pointer',
              opacity: activeFilter !== 'Maintenance' ? 0.5 : 1,
              border: activeFilter === 'Maintenance' ? '1px solid #c62828' : 'none'
            }}
            icon={<Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#c62828', ml: 1 }} />}
          />
          <Chip
            label={`${t('revenue.delay', 'Delay')}: ${summary.delay}`}
            size="small"
            onClick={() => setActiveFilter('Delay')}
            sx={{
              bgcolor: '#fff3e0',
              color: '#e65100',
              fontWeight: 'bold',
              cursor: 'pointer',
              opacity: activeFilter !== 'Delay' ? 0.5 : 1,
              border: activeFilter === 'Delay' ? '1px solid #e65100' : 'none'
            }}
            icon={<Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#e65100', ml: 1 }} />}
          />
          <Chip
            label={`${t('revenue.idle', 'IDLE')}: ${summary.idle}`}
            size="small"
            onClick={() => setActiveFilter('IDLE')}
            sx={{
              bgcolor: '#fff8e1',
              color: '#fbc02d',
              fontWeight: 'bold',
              cursor: 'pointer',
              opacity: activeFilter !== 'IDLE' ? 0.5 : 1,
              border: activeFilter === 'IDLE' ? '1px solid #fbc02d' : 'none'
            }}
            icon={<Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#fbc02d', ml: 1 }} />}
          />
          <Chip
            label={`${t('revenue.off', 'OFF')}: ${summary.off}`}
            size="small"
            onClick={() => setActiveFilter('OFF')}
            sx={{
              bgcolor: '#f5f5f5',
              color: '#424242',
              fontWeight: 'bold',
              cursor: 'pointer',
              opacity: activeFilter !== 'OFF' ? 0.5 : 1,
              border: activeFilter === 'OFF' ? '1px solid #424242' : 'none'
            }}
            icon={<Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#424242', ml: 1 }} />}
          />
        </Stack>
      </Stack>

      <Box
        sx={{
          '& > .MuiPaper-root': {
            height: 550,
            transition: 'height 0.3s ease-in-out',
            boxShadow: 'none',
            border: 'none',
            p: 0
          }
        }}
      >
        <DataTableV2
          rows={filteredData}
          columns={columns}
          loading={loading}
          hideExportButton={false}
          searchQuery={searchConfig.value}
          searchFieldOptions={[
            { label: 'All', value: 'all' },
            { label: 'Driver Name', value: 'driverName' },
            { label: 'Vehicle Number', value: 'vehicleNumber' },
            { label: 'Status Vehicle', value: 'statusVehicle' },
            { label: 'Ritase', value: 'retase' },
            { label: 'Last Update', value: 'lastUpdateRaw' }
          ]}
          onSearch={(val) => {
            setSearchConfig(val);
            setPaginationModel((prev) => ({ ...prev, page: 0 }));
          }}
          title=""
          pagination
          paginationMode="client"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[5, 10, 25, 50]}
        />
      </Box>
    </Card>
  );
}
